import { getAllStatisticsAreaIdentifiers, getEfficiencyAreaIdentifiers } from './statisticsDatasetHelpers';
import { LIVE_FLOW_STAT_SAMPLE_SECONDS } from './machineStatDataConstants';

export const buildEmptyData = (datasets) => Object.fromEntries(getAllStatisticsAreaIdentifiers(datasets).map((areaIdentifier) => [areaIdentifier, []]));

const toNumberOrNull = (value) => {
  if (typeof value === 'number') return value;
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const toAggregatedBoolSignalValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : Boolean(value);
};

const toSignalValue = (value) => {
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  return toNumberOrNull(value);
};

const normalizeDetailsList = (entries, numericKeys) => {
  if (!Array.isArray(entries)) return [];

  const keys = Array.isArray(numericKeys) ? numericKeys : [numericKeys];

  return entries
    .map((entry) => ({
      ...entry,
      ...Object.fromEntries(keys.map((key) => [key, toNumberOrNull(entry?.[key])]))
    }))
    .filter((entry) => entry.name || keys.some((key) => entry[key] !== null));
};

const isActiveBoolValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined || value === '') return false;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue !== 0 : Boolean(value);
};

const buildMappedBoolErrorDetails = (mappedBools, captureRate) => {
  if (!Array.isArray(mappedBools)) return [];

  const sampleSeconds = typeof captureRate === 'number' && Number.isFinite(captureRate) && captureRate > 0 ? captureRate : LIVE_FLOW_STAT_SAMPLE_SECONDS;
  const activeMinutes = sampleSeconds / 60;

  return mappedBools
    .filter((entry) => entry?.name)
    .map((entry) => {
      const minutes = isActiveBoolValue(entry.value) ? activeMinutes : 0;
      const error = { name: entry.name, minutes };
      if (entry.noFlow !== undefined) error.noFlowMinutes = entry.noFlow ? minutes : 0;
      return error;
    });
};

const calculateEfficiency = (aggregateSpeed, expectedSpeed) => {
  if (expectedSpeed <= 0) return null;
  return (aggregateSpeed / expectedSpeed) * 100;
};

export const normalizePoint = (entry) => {
  const aggregateSpeedTotal = toNumberOrNull(entry?.aggregateSpeed);
  const expectedSpeedTotal = toNumberOrNull(entry?.expectedSpeed);
  const contractSpeedTotal = toNumberOrNull(entry?.contractSpeed ?? entry?.data?.contractSpeed);
  const itemCount = toNumberOrNull(entry?.itemCount);
  const captureRate = toNumberOrNull(entry?.captureRate);
  const errors = normalizeDetailsList(entry?.errors, ['minutes', 'noFlowMinutes']).map((error) => ({
    ...error,
    noFlowMinutes: error.noFlowMinutes ?? toNumberOrNull(error.flowMinutes)
  }));
  const mappedErrors = buildMappedBoolErrorDetails(entry?.mappedBools ?? entry?.data?.mappedBools, captureRate);
  const doubles = normalizeDetailsList(entry?.doubles, 'value');
  const mappedDoubles = normalizeDetailsList(entry?.mappedReals ?? entry?.data?.mappedReals, 'value');
  const windowLength = toNumberOrNull(entry?.windowLength ?? entry?.window_length ?? entry?.data?.windowLength ?? entry?.data?.window_length);
  const aggregateSpeed = aggregateSpeedTotal;
  const expectedSpeed = expectedSpeedTotal;
  const contractSpeed = contractSpeedTotal;

  return {
    date: entry?.windowStart ?? entry?.timestamp ?? '',
    durationMinutes: toNumberOrNull(
      entry?.durationMinutes ?? entry?.windowMinutes ?? entry?.bucketMinutes ?? windowLength ?? entry?.data?.durationMinutes ?? entry?.data?.windowMinutes ?? entry?.data?.bucketMinutes
    ),
    aggregateSpeed,
    aggregateSpeedAccumulated: toNumberOrNull(entry?.aggregateSpeedAccumulated),
    runningMetres: toNumberOrNull(entry?.runningMetres),
    actualSpeed: aggregateSpeed,
    expectedSpeed,
    contractSpeed,
    efficiency: calculateEfficiency(aggregateSpeed, expectedSpeed),
    contractEfficiency: calculateEfficiency(aggregateSpeed, contractSpeed),
    itemCount,
    captureRate,
    aggregatedBoolSignals: toAggregatedBoolSignalValue(entry?.aggregatedBoolSignals ?? entry?.data?.aggregatedBoolSignals),
    noFlowMinutes: toAggregatedBoolSignalValue(entry?.noFlowMinutes ?? entry?.data?.noFlowMinutes ?? (entry?.noFlowBools && (entry.noFlow || entry.noFlowBools.some(Boolean)))),
    aggregatedMainSignals: toSignalValue(entry?.aggregatedMainSignals ?? entry?.data?.aggregatedMainSignals),
    materialBefore: toSignalValue(entry?.materialBefore ?? entry?.data?.materialBefore),
    machineAvailable: toSignalValue(entry?.machineAvailable ?? entry?.data?.machineAvailable),
    allowanceForward: toSignalValue(entry?.allowanceForward ?? entry?.data?.allowanceForward),
    pcDataOk: toSignalValue(entry?.pcDataOk ?? entry?.data?.pcDataOk),
    errors: errors.length ? errors : mappedErrors,
    doubles: doubles.length ? doubles : mappedDoubles
  };
};

const sortPointsByDate = (points) =>
  [...points].sort((leftPoint, rightPoint) => {
    const leftTime = Date.parse(leftPoint.date);
    const rightTime = Date.parse(rightPoint.date);

    if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
      return String(leftPoint.date).localeCompare(String(rightPoint.date));
    }

    return leftTime - rightTime;
  });

const dedupePointsByDate = (points) => {
  const pointsByDate = new Map();

  points.forEach((point) => {
    if (!point?.date) return;
    pointsByDate.set(point.date, point);
  });

  return sortPointsByDate([...pointsByDate.values()]);
};

const sumNumericPointValues = (points, key) =>
  points.reduce((sum, point) => {
    const value = point?.[key];
    return typeof value === 'number' && Number.isFinite(value) ? sum + value : sum;
  }, 0);

const hasNumericPointValue = (points, key) =>
  points.some((point) => {
    const value = point?.[key];
    return typeof value === 'number' && Number.isFinite(value);
  });

const averageNumericPointValues = (points, key) => {
  let sum = 0;
  let count = 0;

  points.forEach((point) => {
    const value = point?.[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) return;

    sum += value;
    count += 1;
  });

  return count > 0 ? sum / count : null;
};

const latestPointValue = (points, key) => {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const value = points[index]?.[key];
    if (value !== null && value !== undefined) return value;
  }

  return null;
};

const getAggregatedBoolSignalMinutes = (point) => {
  const value = point?.aggregatedBoolSignals;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value !== true) return 0;

  const captureRateSeconds = point?.captureRate;
  const sampleSeconds = typeof captureRateSeconds === 'number' && Number.isFinite(captureRateSeconds) && captureRateSeconds > 0 ? captureRateSeconds : LIVE_FLOW_STAT_SAMPLE_SECONDS;
  return sampleSeconds / 60;
};

const getNoFlowMinutes = (point) => {
  const value = point?.noFlowMinutes;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value !== true) return 0;

  const captureRateSeconds = point?.captureRate;
  const sampleSeconds = typeof captureRateSeconds === 'number' && Number.isFinite(captureRateSeconds) && captureRateSeconds > 0 ? captureRateSeconds : LIVE_FLOW_STAT_SAMPLE_SECONDS;
  return sampleSeconds / 60;
};

const aggregateDetailsByName = (points, key, numericKeys, averageValues = false) => {
  const totals = new Map();
  const keys = Array.isArray(numericKeys) ? numericKeys : [numericKeys];

  points.forEach((point) => {
    (Array.isArray(point?.[key]) ? point[key] : []).forEach((entry) => {
      const name = entry?.name;
      if (!name) return;

      const total = totals.get(name) ?? Object.fromEntries(keys.map((numericKey) => [numericKey, { sum: 0, count: 0 }]));
      keys.forEach((numericKey) => {
        const value = entry?.[numericKey];
        if (typeof value !== 'number' || !Number.isFinite(value)) return;

        total[numericKey].sum += value;
        total[numericKey].count += 1;
      });
      totals.set(name, total);
    });
  });

  return [...totals.entries()].map(([name, total]) => ({
    name,
    ...Object.fromEntries(keys.map((numericKey) => [numericKey, total[numericKey].count ? (averageValues ? total[numericKey].sum / total[numericKey].count : total[numericKey].sum) : null]))
  }));
};

const toMinuteBucketDate = (dateValue) => {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return String(dateValue).slice(0, 16);
  }

  date.setSeconds(0, 0);
  return date.toISOString();
};

const bucketLivePointsByMinute = (points) => {
  const buckets = new Map();

  sortPointsByDate(points).forEach((point) => {
    const bucketDate = toMinuteBucketDate(point?.date);
    if (!bucketDate) return;

    if (!buckets.has(bucketDate)) {
      buckets.set(bucketDate, []);
    }

    buckets.get(bucketDate).push(point);
  });

  return sortPointsByDate(
    [...buckets.entries()].map(([date, bucketPoints]) => {
      const aggregateSpeed = sumNumericPointValues(bucketPoints, 'aggregateSpeed');
      const expectedSpeed = sumNumericPointValues(bucketPoints, 'expectedSpeed');
      const contractSpeed = hasNumericPointValue(bucketPoints, 'contractSpeed') ? sumNumericPointValues(bucketPoints, 'contractSpeed') : null;
      const itemCount = sumNumericPointValues(bucketPoints, 'itemCount') || bucketPoints.length;
      const materialBefore = sumNumericPointValues(bucketPoints, 'materialBefore');
      const machineAvailable = sumNumericPointValues(bucketPoints, 'machineAvailable');
      const allowanceForward = sumNumericPointValues(bucketPoints, 'allowanceForward');
      const pcDataOk = sumNumericPointValues(bucketPoints, 'pcDataOk');

      return {
        date,
        durationMinutes: 1,
        aggregateSpeed,
        aggregateSpeedAccumulated: latestPointValue(bucketPoints, 'aggregateSpeedAccumulated'),
        runningMetres: latestPointValue(bucketPoints, 'runningMetres'),
        actualSpeed: aggregateSpeed,
        expectedSpeed,
        contractSpeed,
        efficiency: calculateEfficiency(aggregateSpeed, expectedSpeed),
        contractEfficiency: calculateEfficiency(aggregateSpeed, contractSpeed),
        itemCount,
        captureRate: averageNumericPointValues(bucketPoints, 'captureRate'),
        aggregatedBoolSignals: sumPointValues(bucketPoints, getAggregatedBoolSignalMinutes),
        noFlowMinutes: sumPointValues(bucketPoints, getNoFlowMinutes),
        materialBefore,
        machineAvailable,
        allowanceForward,
        pcDataOk,
        errors: aggregateDetailsByName(bucketPoints, 'errors', ['minutes', 'noFlowMinutes']),
        doubles: aggregateDetailsByName(bucketPoints, 'doubles', 'value', true)
      };
    })
  );
};

export const normalizeGroupedResponse = (responseData, datasets) => {
  const groupedData = buildEmptyData(datasets);

  (Array.isArray(responseData) ? responseData : []).forEach((group) => {
    const groupAreaIdentifier = group?.areaIdentifier ?? group?.data?.areaIdentifier ?? group?.data?.[0]?.areaIdentifier ?? group?.data?.[0]?.data?.areaIdentifier;
    const groupIdentifier = group?.identifier;
    const entries = Array.isArray(group?.data) ? group.data : [group];

    entries.forEach((entry) => {
      const areaIdentifier = entry?.areaIdentifier ?? entry?.data?.areaIdentifier ?? groupAreaIdentifier;
      if (!areaIdentifier) return;

      const point = normalizePoint(entry);
      if (!point.date) return;

      if (groupIdentifier) point.identifier = groupIdentifier;

      if (!groupedData[areaIdentifier]) groupedData[areaIdentifier] = [];
      groupedData[areaIdentifier].push(point);
    });
  });

  return Object.fromEntries(Object.entries(groupedData).map(([areaIdentifier, points]) => [areaIdentifier, dedupePointsByDate(points)]));
};

const pruneLivePoints = (points, liveWindowMinutes) => {
  const cutoffTimestamp = Date.now() - liveWindowMinutes * 60 * 1000;

  return points.filter((point) => {
    const pointTimestamp = Date.parse(point?.date);
    return Number.isNaN(pointTimestamp) || pointTimestamp >= cutoffTimestamp;
  });
};

export const mergeLivePoints = (existingPoints, incomingPoints, liveWindowMinutes) => {
  if (!incomingPoints.length) {
    return pruneLivePoints(existingPoints, liveWindowMinutes);
  }

  const pointsByDate = new Map(existingPoints.map((point) => [point.date, point]));

  incomingPoints.forEach((point) => {
    if (!point?.date) return;
    pointsByDate.set(point.date, point);
  });

  return pruneLivePoints(sortPointsByDate([...pointsByDate.values()]), liveWindowMinutes);
};

export const replaceLiveDataByArea = (previousData, incomingData, datasets, liveWindowMinutes) => {
  const mergedData = buildEmptyData(datasets);

  getAllStatisticsAreaIdentifiers(datasets).forEach((areaIdentifier) => {
    const incomingPoints = incomingData?.[areaIdentifier] ?? [];
    const latestIncomingTimestamp = incomingPoints.reduce((latestTimestamp, point) => {
      const timestamp = Date.parse(point?.date);
      return Number.isNaN(timestamp) ? latestTimestamp : Math.max(latestTimestamp, timestamp);
    }, 0);
    const newerExistingPoints = latestIncomingTimestamp
      ? (previousData?.[areaIdentifier] ?? []).filter((point) => {
          const timestamp = Date.parse(point?.date);
          return !Number.isNaN(timestamp) && timestamp > latestIncomingTimestamp;
        })
      : [];

    mergedData[areaIdentifier] = mergeLivePoints(incomingPoints, newerExistingPoints, liveWindowMinutes);
  });

  return mergedData;
};

export const buildDisplayDataByArea = (sourceDataByArea, datasets, rangeMode) => {
  if (rangeMode !== 'live') {
    return sourceDataByArea;
  }

  return Object.fromEntries(
    getAllStatisticsAreaIdentifiers(datasets).map((areaIdentifier) => {
      const rawPoints = sourceDataByArea?.[areaIdentifier] ?? [];
      return [areaIdentifier, bucketLivePointsByMinute(rawPoints)];
    })
  );
};

export const applyCalculatedExpectedSpeed = (dataByArea, dataset, expectedSpeedAverage, lengthEntries = []) => {
  if (!dataset) return dataByArea;

  const entries = lengthEntries
    .filter((entry) => entry.data?.length > 0 && entry.data.width > 0 && entry.data.thickness > 0)
    .map((entry) => ({ time: Date.parse(entry.timestamp), data: entry.data, count: entry.sampleCount ?? 1 }))
    .sort((left, right) => left.time - right.time);
  let dimensionGroup = 0;
  entries.forEach((entry, index) => {
    const previous = entries[index - 1]?.data;
    if (previous && (entry.data.width !== previous.width || entry.data.thickness !== previous.thickness)) dimensionGroup += 1;
    entry.dimensions = { width: entry.data.width, thickness: entry.data.thickness, group: dimensionGroup };
  });
  let entryIndex = 0;
  // Retain the range target before the first measurement, then carry the latest bucket target through idle periods.
  let targetSpeed = expectedSpeedAverage ?? 0;
  let measurements;

  return {
    ...dataByArea,
    [dataset.key]: (dataByArea[dataset.key] ?? []).map((point) => {
      const duration = point.durationMinutes ?? 1;
      const start = Date.parse(point.date);
      const end = start + duration * 60_000;
      while (entryIndex < entries.length && entries[entryIndex].time < start) {
        measurements = entries[entryIndex].data;
        entryIndex += 1;
      }
      const bucketStart = entryIndex;
      let lengthTotal = 0;
      let widthTotal = 0;
      let thicknessTotal = 0;
      let boardCount = 0;
      while (entryIndex < entries.length && entries[entryIndex].time < end) {
        const entry = entries[entryIndex];
        lengthTotal += entry.data.length * entry.count;
        widthTotal += entry.data.width * entry.count;
        thicknessTotal += entry.data.thickness * entry.count;
        boardCount += entry.count;
        entryIndex += 1;
      }
      if (boardCount) {
        const averageLength = lengthTotal / boardCount;
        measurements = { length: averageLength, width: widthTotal / boardCount, thickness: thicknessTotal / boardCount };
        targetSpeed =
          entries.slice(bucketStart, entryIndex).reduce((sum, entry) => sum + dataset.calculateExpectedSpeed(entry.data.width * entry.data.thickness, averageLength) * entry.count, 0) / boardCount;
      }
      // Rates are m/min; chart and summary helpers consume metres per bucket.
      const expectedSpeed = targetSpeed * duration;
      const efficiency = calculateEfficiency(point.actualSpeed, expectedSpeed);
      return {
        ...point,
        expectedSpeed,
        contractSpeed: expectedSpeed,
        efficiency,
        contractEfficiency: efficiency,
        xcutDimensions: entries[entryIndex - 1]?.dimensions ?? null,
        averageXcutLength: measurements?.length ?? null,
        averageXcutWidth: measurements?.width ?? null,
        averageXcutThickness: measurements?.thickness ?? null,
        xcutDataCarriedForward: !boardCount && Boolean(measurements)
      };
    })
  };
};

export const hasExistingDatasetData = (dataByArea, datasets) => getAllStatisticsAreaIdentifiers(datasets).some((areaIdentifier) => (dataByArea?.[areaIdentifier] ?? []).length > 0);

export const filterDataByRecentMinutes = (dataByArea, minutes) => {
  if (!minutes || minutes <= 0) return dataByArea;

  const latestTimestamp = Object.values(dataByArea ?? {}).reduce((latest, points) => {
    (Array.isArray(points) ? points : []).forEach((point) => {
      const timestamp = Date.parse(point?.date);
      if (!Number.isNaN(timestamp)) {
        latest = Math.max(latest, timestamp);
      }
    });

    return latest;
  }, 0);

  if (!latestTimestamp) return dataByArea;

  const cutoffTimestamp = latestTimestamp - minutes * 60 * 1000;
  return Object.fromEntries(
    Object.entries(dataByArea ?? {}).map(([areaIdentifier, points]) => [
      areaIdentifier,
      (Array.isArray(points) ? points : []).filter((point) => {
        const timestamp = Date.parse(point?.date);
        return !Number.isNaN(timestamp) && timestamp >= cutoffTimestamp;
      })
    ])
  );
};

const sumPointValues = (points, getValue) =>
  points.reduce((sum, point) => {
    const value = getValue(point);
    return typeof value === 'number' && Number.isFinite(value) ? sum + value : sum;
  }, 0);

const countPointValues = (points, getValue) =>
  points.reduce((count, point) => {
    const value = getValue(point);
    return typeof value === 'number' && Number.isFinite(value) ? count + 1 : count;
  }, 0);

const speedAverageValue = (point, key) => {
  const value = point?.[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;

  const durationMinutes = point?.durationMinutes;
  return typeof durationMinutes === 'number' && Number.isFinite(durationMinutes) && durationMinutes > 1 ? value / durationMinutes : value;
};

const calculateEfficiencyForPoints = (points, denominatorKey) => {
  let actualSpeedSum = 0;
  let denominatorSum = 0;

  points.forEach((point) => {
    const actualSpeed = point?.actualSpeed;
    const denominator = point?.[denominatorKey];
    if (typeof actualSpeed !== 'number' || !Number.isFinite(actualSpeed) || typeof denominator !== 'number' || !Number.isFinite(denominator) || denominator <= 0) return;

    actualSpeedSum += actualSpeed;
    denominatorSum += denominator;
  });

  return calculateEfficiency(actualSpeedSum, denominatorSum);
};

const buildCombinedEfficiencyPoints = (dataByArea, areaIdentifiers) => {
  const pointsByDate = new Map();

  areaIdentifiers.forEach((areaIdentifier) => {
    (dataByArea?.[areaIdentifier] ?? []).forEach((point) => {
      if (!point?.date) return;

      if (!pointsByDate.has(point.date)) {
        pointsByDate.set(point.date, {
          date: point.date,
          durationMinutes: point.durationMinutes,
          actualSpeed: 0,
          expectedSpeed: 0,
          contractSpeed: 0,
          hasContractSpeed: false
        });
      }

      const combinedPoint = pointsByDate.get(point.date);
      combinedPoint.runningMetres = point.runningMetres ?? combinedPoint.runningMetres;
      if (typeof point.durationMinutes === 'number' && Number.isFinite(point.durationMinutes)) {
        combinedPoint.durationMinutes = Math.max(combinedPoint.durationMinutes ?? 0, point.durationMinutes);
      }
      combinedPoint.actualSpeed += typeof point.actualSpeed === 'number' && Number.isFinite(point.actualSpeed) ? point.actualSpeed : 0;
      combinedPoint.expectedSpeed += typeof point.expectedSpeed === 'number' && Number.isFinite(point.expectedSpeed) ? point.expectedSpeed : 0;
      if (typeof point.contractSpeed === 'number' && Number.isFinite(point.contractSpeed)) {
        combinedPoint.contractSpeed += point.contractSpeed;
        combinedPoint.hasContractSpeed = true;
      }
    });
  });

  return sortPointsByDate(
    [...pointsByDate.values()].map((point) => {
      const contractSpeed = point.hasContractSpeed ? point.contractSpeed : null;

      return {
        ...point,
        contractSpeed,
        aggregateSpeed: point.actualSpeed,
        efficiency: calculateEfficiency(point.actualSpeed, point.expectedSpeed),
        contractEfficiency: calculateEfficiency(point.actualSpeed, contractSpeed)
      };
    })
  );
};

export const buildSummaryByArea = (dataByArea, datasets) =>
  Object.fromEntries(
    (datasets ?? []).map((dataset) => {
      const points = buildCombinedEfficiencyPoints(dataByArea, getEfficiencyAreaIdentifiers(dataset));
      const latestPoint = points[points.length - 1] ?? null;
      const byMinute = Boolean(dataset.calculateExpectedSpeed);
      const speedCalculations = Object.fromEntries(
        ['actualSpeed', 'expectedSpeed', 'contractSpeed'].map((field) => {
          const total = sumPointValues(points, (point) => (byMinute ? point[field] : speedAverageValue(point, field)));
          const divisor = byMinute
            ? sumPointValues(points, (point) => (speedAverageValue(point, field) !== null ? point.durationMinutes ?? 1 : null))
            : countPointValues(points, (point) => speedAverageValue(point, field));
          return [field, { total, divisor, basis: byMinute ? 'minutes' : 'buckets' }];
        })
      );
      const averageSpeed = (field) => {
        const { total, divisor } = speedCalculations[field];
        return divisor > 0 ? total / divisor : null;
      };

      const fullRangeEfficiency = calculateEfficiencyForPoints(points, 'expectedSpeed');
      const fullRangeContractEfficiency = calculateEfficiencyForPoints(points, 'contractSpeed');

      return [
        dataset.key,
        {
          fullRangeEfficiency,
          fullRangeContractEfficiency,
          speedCalculations,
          actualSpeedAverage: averageSpeed('actualSpeed'),
          expectedSpeedAverage: averageSpeed('expectedSpeed'),
          contractSpeedAverage: averageSpeed('contractSpeed'),
          latestRunningMetres: latestPoint?.runningMetres ?? null,
          latestActualSpeed: speedAverageValue(latestPoint, 'actualSpeed'),
          latestExpectedSpeed: speedAverageValue(latestPoint, 'expectedSpeed'),
          latestContractSpeed: speedAverageValue(latestPoint, 'contractSpeed')
        }
      ];
    })
  );
